import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  Message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "@arco-design/web-react";
import { IconDelete, IconPlus, IconEdit } from "@arco-design/web-react/icon";

import { FetchApi, PostApi, OverwriteApi, DeleteApi } from "../../Api/AuthenticatedApi";

const { Text } = Typography;
const { TextArea } = Input;

const PERIOD_OPTIONS = [
  { label: "One-time", value: "one_time" },
  { label: "/ month", value: "month" },
  { label: "/ year", value: "year" },
];
const PERIOD_SUFFIX = { one_time: "", month: "/mo", year: "/yr" };

let localKeySeq = 0;
const nextLocalKey = () => `new-${localKeySeq++}`;

const fmtPrice = (p) =>
  "$" + (p.unit_amount_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 }) + (PERIOD_SUFFIX[p.billing_period] || "");

// Price rows hold dollars (the API stores cents).
function toPriceRow(p) {
  return {
    key: p.id || nextLocalKey(),
    id: p.id,
    amount: p.unit_amount_cents == null ? undefined : p.unit_amount_cents / 100,
    billing_period: p.billing_period || "one_time",
  };
}

function pricesPayload(rows) {
  return rows
    .filter((r) => r.amount != null && r.amount !== "")
    .map((r) => {
      const p = {
        unit_amount_cents: Math.round(Number(r.amount) * 100),
        billing_period: r.billing_period || "one_time",
        currency: "usd",
        active: true,
      };
      if (r.id) p.id = r.id;
      return p;
    });
}

export default function ProductSettings() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [products, setProducts] = useState([]);
  const [refresh, setRefresh] = useState(true);

  const [editing, setEditing] = useState(null); // {} = new, product = edit, null = closed
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [isRecurring, setIsRecurring] = useState(false);
  const [priceRows, setPriceRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!refresh) return;
    FetchApi(getAccessTokenSilently, "/api/products/",
      (data) => { setProducts(data || []); setRefresh(false); },
      () => navigate("/login"),
      () => { setRefresh(false); Message.error("Failed to load products"); });
  }, [getAccessTokenSilently, navigate, refresh]);

  const openNew = () => {
    setEditing({}); setName(""); setDescription(""); setActive(true); setIsRecurring(false);
    setPriceRows([toPriceRow({ billing_period: "one_time" })]);
  };

  const openEdit = (p) => {
    setEditing(p); setName(p.name || ""); setDescription(p.description || "");
    setActive(p.active !== false); setIsRecurring(!!p.is_recurring);
    setPriceRows((p.prices || []).map(toPriceRow));
  };

  const closeEditor = () => { setEditing(null); setSaving(false); };

  const addPrice = () => setPriceRows((rows) => [...rows, toPriceRow({ billing_period: isRecurring ? "month" : "one_time" })]);
  const updatePrice = (idx, patch) => setPriceRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removePrice = (idx) => setPriceRows((rows) => rows.filter((_, i) => i !== idx));

  const save = () => {
    if (!name.trim()) { Message.warning("Give the product a name"); return; }
    setSaving(true);
    const body = {
      name: name.trim(),
      description: description.trim(),
      active,
      is_recurring: isRecurring,
      prices: pricesPayload(priceRows),
    };
    const onOk = () => { Message.success("Product saved"); closeEditor(); setRefresh(true); };
    const onErr = (err) => { setSaving(false); Message.error((err && err.message) || "Failed to save product"); };
    if (editing && editing.id) {
      OverwriteApi(getAccessTokenSilently, "/api/products/" + editing.id, onOk, () => navigate("/login"), onErr, body);
    } else {
      PostApi(getAccessTokenSilently, "/api/products/", onOk, () => navigate("/login"), onErr, body);
    }
  };

  const remove = (p) => {
    Modal.confirm({
      title: "Delete product",
      content: `Delete "${p.name}"?`,
      okButtonProps: { status: "danger" },
      onOk: () => new Promise((resolve) => {
        DeleteApi(getAccessTokenSilently, "/api/products/" + p.id,
          () => { Message.success("Product deleted"); setRefresh(true); resolve(); },
          () => navigate("/login"),
          () => { Message.error("Failed to delete product"); resolve(); },
          {});
      }),
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (n, p) => (
        <span>
          <Text style={{ fontWeight: 500 }}>{n}</Text>
          {p.active === false && <Tag size="small" color="gray" style={{ marginLeft: 6 }}>inactive</Tag>}
          {p.is_recurring && <Tag size="small" color="arcoblue" style={{ marginLeft: 6 }}>recurring</Tag>}
        </span>
      ),
    },
    {
      title: "Prices",
      dataIndex: "prices",
      render: (prices) => ((prices || []).length
        ? prices.map((p) => <Tag key={p.id} size="small" style={{ marginRight: 4 }}>{fmtPrice(p)}</Tag>)
        : <Text type="secondary">No price</Text>),
    },
    {
      title: "",
      width: 96,
      render: (_, p) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          <Button size="small" type="text" shape="circle" icon={<IconEdit />} onClick={() => openEdit(p)} />
          <Button size="small" type="text" shape="circle" icon={<IconDelete />} onClick={() => remove(p)} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Text type="secondary">
        Your product catalog. Focus a campaign on a product, and (once Stripe is connected) match real
        purchases back to it. Mark subscriptions as <b>recurring</b>.
      </Text>

      <Table
        style={{ marginTop: 16 }}
        size="small"
        columns={columns}
        data={products}
        rowKey="id"
        pagination={false}
        border={false}
        noDataElement={<span style={{ color: "var(--color-text-3)" }}>No products yet.</span>}
      />

      <div style={{ marginTop: 16 }}>
        <Button type="primary" icon={<IconPlus />} onClick={openNew}>New product</Button>
      </div>

      <Modal
        title={editing && editing.id ? "Edit product" : "New product"}
        visible={!!editing}
        onCancel={closeEditor}
        footer={
          <Space>
            <Button onClick={closeEditor}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={save}>Save</Button>
          </Space>
        }
        style={{ width: 620 }}
        unmountOnExit
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Name</Text></div>
            <Input placeholder="e.g. Pro Plan" value={name} onChange={setName} />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Description</Text></div>
            <TextArea placeholder="Optional" value={description} onChange={setDescription} autoSize={{ minRows: 1 }} />
          </div>
          <Space size={24}>
            <Checkbox checked={isRecurring} onChange={setIsRecurring}>Recurring (subscription)</Checkbox>
            <Checkbox checked={active} onChange={setActive}>Active</Checkbox>
          </Space>

          <div>
            <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Prices</Text></div>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {priceRows.map((row, idx) => (
                <div key={row.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <InputNumber
                    style={{ width: 160 }}
                    prefix="$"
                    min={0}
                    precision={2}
                    placeholder="Amount"
                    value={row.amount}
                    onChange={(v) => updatePrice(idx, { amount: v })}
                  />
                  <Select
                    style={{ width: 140 }}
                    value={row.billing_period}
                    onChange={(v) => updatePrice(idx, { billing_period: v })}
                    options={PERIOD_OPTIONS}
                  />
                  <Button size="small" type="text" shape="circle" icon={<IconDelete />} onClick={() => removePrice(idx)} />
                </div>
              ))}
            </Space>
            <div style={{ marginTop: 8 }}>
              <Button size="small" type="outline" icon={<IconPlus />} onClick={addPrice}>Add price</Button>
            </div>
          </div>
        </Space>
      </Modal>
    </div>
  );
}
